import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../tokens';

/**
 * PageLayout - Main layout component for public pages
 * Provides consistent spacing, max-width containment, and responsive behavior
 */

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ $background }) => {
    switch ($background) {
      case 'secondary':
        return theme.colors.background.secondary;
      case 'tertiary':
        return theme.colors.background.tertiary;
      case 'gradient':
        return theme.gradients.light;
      default:
        return theme.colors.background.primary;
    }
  }};
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  width: 100%;
  max-width: ${({ $maxWidth }) => {
    switch ($maxWidth) {
      case 'sm':
        return theme.containerWidths.sm;
      case 'md':
        return theme.containerWidths.md;
      case 'lg':
        return theme.containerWidths.lg;
      case 'xl':
        return theme.containerWidths.xl;
      case 'full':
        return '100%';
      default:
        return theme.containerWidths['2xl'];
    }
  }};
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};

  ${theme.media.md} {
    padding: 0 ${theme.spacing[6]};
  }

  ${theme.media.lg} {
    padding: 0 ${theme.spacing[8]};
  }
`;

const Section = styled.section`
  padding: ${theme.spacing[8]} 0;

  ${theme.media.md} {
    padding: ${theme.spacing[12]} 0;
  }

  ${theme.media.lg} {
    padding: ${theme.spacing[16]} 0;
  }

  ${({ $noPadding }) =>
    $noPadding &&
    css`
      padding: 0;
    `}

  ${({ $background }) => {
    switch ($background) {
      case 'primary':
        return css`
          background: ${theme.colors.background.primary};
        `;
      case 'secondary':
        return css`
          background: ${theme.colors.background.secondary};
        `;
      case 'tertiary':
        return css`
          background: ${theme.colors.background.tertiary};
        `;
      case 'gradient':
        return css`
          background: ${theme.gradients.primary};
          color: ${theme.colors.text.inverse};
        `;
      case 'dark':
        return css`
          background: ${theme.colors.neutral[900]};
          color: ${theme.colors.text.inverse};
        `;
      default:
        return '';
    }
  }}
`;

const Hero = styled.div`
  background: ${theme.gradients.primary};
  color: ${theme.colors.text.inverse};
  padding: ${theme.spacing[12]} 0;
  position: relative;
  overflow: hidden;

  ${theme.media.md} {
    padding: ${theme.spacing[16]} 0;
  }

  ${theme.media.lg} {
    padding: ${theme.spacing[20]} 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const Grid = styled.div`
  display: grid;
  gap: ${({ $gap }) => {
    switch ($gap) {
      case 'xs':
        return theme.spacing[2];
      case 'sm':
        return theme.spacing[4];
      case 'lg':
        return theme.spacing[8];
      case 'xl':
        return theme.spacing[12];
      default:
        return theme.spacing[6];
    }
  }};

  ${({ $cols }) => {
    if (typeof $cols === 'number') {
      return css`
        grid-template-columns: repeat(${$cols}, 1fr);
      `;
    }
    // Responsive columns
    return css`
      grid-template-columns: 1fr;

      ${theme.media.sm} {
        grid-template-columns: repeat(${$cols?.sm || 1}, 1fr);
      }

      ${theme.media.md} {
        grid-template-columns: repeat(${$cols?.md || 2}, 1fr);
      }

      ${theme.media.lg} {
        grid-template-columns: repeat(${$cols?.lg || 3}, 1fr);
      }

      ${theme.media.xl} {
        grid-template-columns: repeat(${$cols?.xl || $cols?.lg || 4}, 1fr);
      }
    `;
  }}
`;

const TwoColumn = styled.div`
  display: grid;
  gap: ${theme.spacing[8]};
  grid-template-columns: 1fr;

  ${theme.media.lg} {
    grid-template-columns: ${({ $sidebarWidth }) => $sidebarWidth || '300px'} 1fr;
  }

  ${({ $reversed }) =>
    $reversed &&
    css`
      ${theme.media.lg} {
        grid-template-columns: 1fr ${({ $sidebarWidth }) => $sidebarWidth || '300px'};
      }
    `}
`;

const Sidebar = styled.aside`
  ${theme.media.lg} {
    position: sticky;
    top: ${theme.spacing[6]};
    height: fit-content;
  }
`;

const ContentArea = styled.div`
  min-width: 0; /* Prevent grid blowout */
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: ${theme.colors.border.light};
  margin: ${({ $spacing }) => {
    switch ($spacing) {
      case 'sm':
        return `${theme.spacing[4]} 0`;
      case 'lg':
        return `${theme.spacing[12]} 0`;
      case 'xl':
        return `${theme.spacing[16]} 0`;
      default:
        return `${theme.spacing[8]} 0`;
    }
  }};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => {
    switch ($gap) {
      case 'xs':
        return theme.spacing[1];
      case 'sm':
        return theme.spacing[2];
      case 'lg':
        return theme.spacing[6];
      case 'xl':
        return theme.spacing[8];
      default:
        return theme.spacing[4];
    }
  }};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  align-items: ${({ $align }) => $align || 'center'};
  justify-content: ${({ $justify }) => $justify || 'flex-start'};
  gap: ${({ $gap }) => {
    switch ($gap) {
      case 'xs':
        return theme.spacing[1];
      case 'sm':
        return theme.spacing[2];
      case 'lg':
        return theme.spacing[6];
      case 'xl':
        return theme.spacing[8];
      default:
        return theme.spacing[4];
    }
  }};
`;

export const PageLayout = ({ children, background = 'primary' }) => {
  return (
    <PageWrapper $background={background}>
      <Main>{children}</Main>
    </PageWrapper>
  );
};

// Attach sub-components
PageLayout.Container = Container;
PageLayout.Section = Section;
PageLayout.Hero = Hero;
PageLayout.HeroContent = HeroContent;
PageLayout.Grid = Grid;
PageLayout.TwoColumn = TwoColumn;
PageLayout.Sidebar = Sidebar;
PageLayout.ContentArea = ContentArea;
PageLayout.Divider = Divider;
PageLayout.Stack = Stack;
PageLayout.Row = Row;

PageLayout.displayName = 'PageLayout';

export default PageLayout;
